import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchSmartyAddressSuggestions,
  SmartyAddressSuggestion,
  SmartyVerificationResult,
  verifySmartyAddress,
} from '../services/smartyAddress';
import { PayerIdentityDraft } from '../services/payerSetupState';
import {
  normalizeAddressSuggestionQuery,
  shouldFetchAddressSuggestions,
} from '../services/addressSuggestionPolicy.mjs';
import {
  normalizeIdentityDobSegment,
  shouldAutoAdvanceIdentityDobSegment,
  validateIdentityDob,
} from '../services/identityFormValidation.mjs';

type Props = {
  visible: boolean;
  value: PayerIdentityDraft;
  onChange: (next: PayerIdentityDraft) => void;
  onClose: () => void;
  onSubmit: () => void;
  busy?: boolean;
  submitError?: string | null;
};

type AddressVerificationState = 'idle' | 'verifying' | 'verified' | 'needs_review';

const TOTAL_STEPS = 3;
const ASTRA_TERMS_URL = 'https://astrafi.com/terms/';
const ASTRA_PRIVACY_URL = 'https://astrafi.com/privacy/';

const STEP_COPY = {
  1: {
    label: 'Identity',
    title: 'Let’s verify your identity',
    microcopy: 'So you can start sending and receiving money',
  },
  2: {
    label: 'Address',
    title: 'Where do you live?',
    microcopy: 'Used only to verify your identity and prevent fraud.\nWe never store your sensitive information.',
  },
  3: {
    label: 'Verification',
    title: 'Secure your account',
    microcopy: 'Required to send and receive payments',
  },
} as const;

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
] as const;

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inferCurrentStep = (draft: PayerIdentityDraft) => {
  if (!draft.firstName.trim() || !draft.lastName.trim() || !draft.email.trim()) return 1;
  if (!draft.addressLine1.trim() || !draft.city.trim() || !draft.state.trim() || draft.postalCode.trim().length !== 5) return 2;
  return 3;
};

const Input = ({
  value,
  onChange,
  placeholder,
  maxLength,
  inputMode,
  type = 'text',
  autoComplete,
  inputRef,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  maxLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  type?: string;
  autoComplete?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  className?: string;
}) => (
  <input
    ref={inputRef}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    maxLength={maxLength}
    inputMode={inputMode}
    type={type}
    autoComplete={autoComplete}
    className={`w-full h-14 rounded-[20px] border border-slate-200 bg-white px-4 text-[16px] font-medium text-slate-900 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100 ${className}`}
  />
);

const PayerIdentityVerificationSheet: React.FC<Props> = ({
  visible,
  value,
  onChange,
  onClose,
  onSubmit,
  busy = false,
  submitError = null,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [stepError, setStepError] = useState<string | null>(null);
  const [consentCaptured, setConsentCaptured] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<SmartyAddressSuggestion[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressVerificationState, setAddressVerificationState] = useState<AddressVerificationState>('idle');
  const [addressVerificationMessage, setAddressVerificationMessage] = useState('');
  const [addressSuggestedCorrection, setAddressSuggestedCorrection] = useState<SmartyVerificationResult['verifiedAddress'] | null>(null);
  const [showStateSuggestions, setShowStateSuggestions] = useState(false);
  const selectedAddressQueryRef = useRef('');
  const dayInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);
  const ssnInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!visible) return;
    setCurrentStep(inferCurrentStep(value));
    setStepError(null);
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);
    setShowStateSuggestions(false);
  }, [visible]);

  useEffect(() => {
    if (!visible || !submitError) return;
    setCurrentStep(3);
    setStepError(submitError);
  }, [submitError, visible]);

  useEffect(() => {
    if (!visible) return;
    const query = normalizeAddressSuggestionQuery(value.addressLine1);
    if (!shouldFetchAddressSuggestions({
      visible,
      currentStep,
      query,
      suppressedQuery: selectedAddressQueryRef.current,
    })) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      setAddressLoading(false);
      return;
    }

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      try {
        setAddressLoading(true);
        const suggestions = await fetchSmartyAddressSuggestions(query);
        if (cancelled) return;
        if (!shouldFetchAddressSuggestions({
          visible,
          currentStep,
          query,
          suppressedQuery: selectedAddressQueryRef.current,
        })) {
          setAddressSuggestions([]);
          setShowAddressSuggestions(false);
          return;
        }
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(suggestions.length > 0);
      } catch {
        if (cancelled) return;
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      } finally {
        if (!cancelled) setAddressLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [currentStep, value.addressLine1, visible]);

  const update = (patch: Partial<PayerIdentityDraft>) => {
    onChange({ ...value, ...patch });
  };

  const normalizedStateQuery = value.state.trim().toUpperCase();
  const filteredStates = useMemo(() => {
    if (!normalizedStateQuery) return US_STATES;
    return US_STATES.filter((state) => (
      state.code.includes(normalizedStateQuery)
      || state.name.toUpperCase().includes(normalizedStateQuery)
    )).slice(0, 8);
  }, [normalizedStateQuery]);

  const currentStepCopy = STEP_COPY[currentStep as keyof typeof STEP_COPY];
  const progressPercent = `${(currentStep / TOTAL_STEPS) * 100}%`;
  const liveDobValidation = useMemo(() => {
    if (!value.dobMonth.trim() || !value.dobDay.trim() || value.dobYear.trim().length !== 4) return null;
    return validateIdentityDob({
      month: value.dobMonth,
      day: value.dobDay,
      year: value.dobYear,
    });
  }, [value.dobDay, value.dobMonth, value.dobYear]);
  const liveDobError = liveDobValidation && !liveDobValidation.valid
    ? liveDobValidation.message || 'A valid date of birth is required.'
    : null;

  if (!visible) return null;

  const clearAddressVerification = () => {
    setAddressVerificationState('idle');
    setAddressVerificationMessage('');
    setAddressSuggestedCorrection(null);
  };

  const applyVerifiedAddress = (verifiedAddress: SmartyVerificationResult['verifiedAddress']) => {
    if (!verifiedAddress) return;
    selectedAddressQueryRef.current = normalizeAddressSuggestionQuery(verifiedAddress.addressLine1);
    update({
      addressLine1: verifiedAddress.addressLine1,
      addressLine2: verifiedAddress.addressLine2 || '',
      city: verifiedAddress.city,
      state: verifiedAddress.state,
      postalCode: verifiedAddress.postalCode,
    });
    setAddressSuggestedCorrection(null);
    setAddressVerificationState('verified');
    setAddressVerificationMessage('Address verified.');
    setShowAddressSuggestions(false);
    setShowStateSuggestions(false);
  };

  const verifySelectedAddress = async (addressOverride?: {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    postalCode: string;
  }) => {
    const currentAddress = addressOverride || {
      addressLine1: value.addressLine1.trim(),
      addressLine2: (value.addressLine2 || '').trim(),
      city: value.city.trim(),
      state: value.state.trim(),
      postalCode: value.postalCode.trim(),
    };
    if (!currentAddress.addressLine1 || !currentAddress.city || !currentAddress.state || currentAddress.postalCode.length !== 5) {
      setStepError('Please complete your home address.');
      return false;
    }

    try {
      setAddressVerificationState('verifying');
      setAddressVerificationMessage('Verifying address...');
      setAddressSuggestedCorrection(null);
      const result = await verifySmartyAddress(currentAddress);
      if (result.verified && result.verifiedAddress) {
        applyVerifiedAddress(result.verifiedAddress);
        return true;
      }

      setAddressVerificationState('needs_review');
      setAddressVerificationMessage(result.message || 'Please review this address before continuing.');
      setAddressSuggestedCorrection(result.verifiedAddress || null);
      setStepError(result.message || 'Please review this address before continuing.');
      return false;
    } catch {
      setAddressVerificationState('needs_review');
      setAddressVerificationMessage('We could not verify this address right now. Please review it.');
      setAddressSuggestedCorrection(null);
      setStepError('We could not verify this address right now. Please review it and try again.');
      return false;
    }
  };

  const handleContinue = async () => {
    setStepError(null);

    if (currentStep === 1) {
      if (!value.firstName.trim() || !value.lastName.trim()) {
        setStepError('Please enter your legal first and last name.');
        return;
      }
      if (!value.email.trim()) {
        setStepError('Please enter your email address.');
        return;
      }
      if (!emailPattern.test(value.email.trim().toLowerCase())) {
        setStepError('Please enter a valid email address.');
        return;
      }
      setCurrentStep(2);
      return;
    }

    if (currentStep === 2) {
      const verified = addressVerificationState === 'verified' ? true : await verifySelectedAddress();
      if (verified) setCurrentStep(3);
      return;
    }

    if (!value.dobMonth.trim() || !value.dobDay.trim() || value.dobYear.trim().length !== 4) {
      setStepError('Please complete your date of birth.');
      return;
    }
    const dobValidation = validateIdentityDob({
      month: value.dobMonth,
      day: value.dobDay,
      year: value.dobYear,
    });
    if (!dobValidation.valid) {
      setStepError(null);
      return;
    }
    if (value.ssnLast4.trim().length !== 4) {
      setStepError('Please enter the last 4 digits of your SSN.');
      return;
    }
    if (!consentCaptured) {
      setStepError('Please confirm that you consent to Astra ACH payment authorization before continuing.');
      return;
    }
    onSubmit();
  };

  const handleAddressSuggestionPress = async (suggestion: SmartyAddressSuggestion) => {
    const selectedAddress = {
      addressLine1: suggestion.streetLine,
      addressLine2: suggestion.secondary || '',
      city: suggestion.city,
      state: suggestion.state,
      postalCode: suggestion.postalCode,
    };
    selectedAddressQueryRef.current = normalizeAddressSuggestionQuery(suggestion.streetLine);
    update(selectedAddress);
    setAddressSuggestions([]);
    setShowAddressSuggestions(false);

    if (suggestion.entries && suggestion.entries > 1) {
      setAddressVerificationState('needs_review');
      setAddressVerificationMessage('Please add your apartment, suite, or unit before continuing.');
      setStepError('This address appears to have multiple unit options. Please add your apartment, suite, or unit number.');
      return;
    }

    setStepError(null);
    await verifySelectedAddress(selectedAddress);
  };

  return (
    <div className="fixed inset-0 z-[95] bg-slate-950/55 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-[36rem] rounded-[2rem] border border-slate-200 bg-[#fcfcfb] shadow-[0_32px_90px_rgba(15,23,42,0.22)] max-h-[min(92vh,820px)] overflow-hidden flex flex-col">
        <div className="border-b border-slate-100 bg-white/90 px-5 py-4 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="mt-1 text-[24px] leading-tight font-black tracking-tight text-slate-900">{currentStepCopy.title}</h3>
              <p className="mt-2 whitespace-pre-line text-[14px] leading-6 text-slate-500">{currentStepCopy.microcopy}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-10 h-10 rounded-full border border-slate-200 bg-white text-slate-500 text-sm transition hover:border-slate-300 hover:text-slate-700"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="mt-5 flex items-center justify-between gap-4">
            <span className="text-[12px] font-semibold text-slate-500">Step {currentStep} of {TOTAL_STEPS}</span>
            <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#14B8A6] to-[#0F766E] transition-all duration-300"
                style={{ width: progressPercent }}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-5 pb-24 sm:px-6 sm:py-6 sm:pb-28 space-y-5">
          <div className="rounded-[1.5rem] border border-[#CFF7F2] bg-[#F2FCFA] px-4 py-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">{currentStepCopy.label}</p>
            <p className="mt-2 text-[14px] leading-6 text-slate-600">
              {currentStep === 1 && 'This helps us verify it’s really you before money starts moving.'}
              {currentStep === 2 && 'We use your address only to verify identity and prevent fraud. Sensitive information is handled securely.'}
              {currentStep === 3 && 'A few final details let Astra approve secure ACH payments from your bank account.'}
            </p>
          </div>

          {stepError ? (
            <div className="rounded-[1.25rem] border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-700">
              {stepError}
            </div>
          ) : null}

          {currentStep === 1 ? (
            <div className="space-y-4">
              <div
                role="note"
                aria-label="Use your legal name. Enter your name exactly as it appears on your government-issued ID."
                className="flex items-start gap-3 rounded-[1.25rem] border border-teal-100 bg-teal-50/80 px-4 py-4"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-teal-700 shadow-sm ring-1 ring-teal-100">
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-6 w-6"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <circle cx="8" cy="10" r="2" />
                    <path d="M5.5 15c.7-1.4 1.5-2 2.5-2s1.8.6 2.5 2M13 9h5M13 13h5" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <p className="text-[14px] font-bold text-slate-900">Use your legal name</p>
                  <p className="mt-1 text-[13px] leading-5 text-slate-600">
                    Enter your name exactly as it appears on your government-issued ID.
                  </p>
                </div>
              </div>
              <div>
                <p className="mb-2 text-[13px] font-semibold text-slate-700">Legal First Name</p>
                <Input
                  value={value.firstName}
                  onChange={(next) => update({ firstName: next })}
                  placeholder="Legal first name"
                  autoComplete="given-name"
                />
              </div>
              <div>
                <p className="mb-2 text-[13px] font-semibold text-slate-700">Legal Last Name</p>
                <Input
                  value={value.lastName}
                  onChange={(next) => update({ lastName: next })}
                  placeholder="Legal last name"
                  autoComplete="family-name"
                />
              </div>
              <div>
                <p className="mb-2 text-[13px] font-semibold text-slate-700">Email</p>
                <Input
                  value={value.email}
                  onChange={(next) => update({ email: next })}
                  placeholder="you@example.com"
                  inputMode="email"
                  autoComplete="email"
                />
              </div>
            </div>
          ) : null}

          {currentStep === 2 ? (
            <div className="space-y-4">
              <div className="relative">
                <p className="mb-2 text-[13px] font-semibold text-slate-700">Address line 1</p>
                <Input
                  value={value.addressLine1}
                  onChange={(next) => {
                    selectedAddressQueryRef.current = '';
                    update({ addressLine1: next });
                    clearAddressVerification();
                  }}
                  placeholder="123 Main St"
                  autoComplete="address-line1"
                />
                {addressLoading ? (
                  <p className="mt-2 text-[13px] text-teal-700">Finding addresses...</p>
                ) : null}
                {showAddressSuggestions && addressSuggestions.length > 0 ? (
                  <div className="mt-2 overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white shadow-sm">
                    {addressSuggestions.map((suggestion, index) => (
                      <button
                        key={`${suggestion.label}-${index}`}
                        type="button"
                        onClick={() => void handleAddressSuggestionPress(suggestion)}
                        className="w-full px-4 py-3 text-left transition hover:bg-slate-50"
                      >
                        <p className="text-[14px] font-semibold text-slate-900">{suggestion.streetLine}</p>
                        <p className="mt-1 text-[13px] text-slate-500">
                          {[suggestion.secondary, suggestion.city, suggestion.state, suggestion.postalCode].filter(Boolean).join(', ')}
                        </p>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              <div>
                <p className="mb-2 text-[13px] font-semibold text-slate-700">Apt, suite, unit (optional)</p>
                <Input
                  value={value.addressLine2 || ''}
                  onChange={(next) => {
                    update({ addressLine2: next });
                    clearAddressVerification();
                  }}
                  placeholder="Apartment, suite, unit"
                  autoComplete="address-line2"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.4fr_0.8fr_0.9fr]">
                <div>
                  <p className="mb-2 text-[13px] font-semibold text-slate-700">City</p>
                  <Input
                    value={value.city}
                    onChange={(next) => {
                      update({ city: next });
                      clearAddressVerification();
                    }}
                    placeholder="San Francisco"
                    autoComplete="address-level2"
                  />
                </div>
                <div className="relative">
                  <p className="mb-2 text-[13px] font-semibold text-slate-700">State</p>
                  <Input
                    value={value.state}
                    onChange={(next) => {
                      update({ state: next.toUpperCase() });
                      clearAddressVerification();
                      setShowStateSuggestions(true);
                    }}
                    placeholder="CA"
                    maxLength={20}
                    autoComplete="address-level1"
                  />
                  {showStateSuggestions && filteredStates.length > 0 ? (
                    <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-10 overflow-hidden rounded-[1.25rem] border border-slate-200 bg-white shadow-sm">
                      {filteredStates.map((state) => (
                        <button
                          key={state.code}
                          type="button"
                          onClick={() => {
                            update({ state: state.code });
                            setShowStateSuggestions(false);
                          }}
                          className="w-full px-4 py-3 text-left text-[14px] font-semibold text-slate-900 transition hover:bg-slate-50"
                        >
                          {state.code}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div>
                  <p className="mb-2 text-[13px] font-semibold text-slate-700">Zip</p>
                  <Input
                    value={value.postalCode}
                    onChange={(next) => {
                      update({ postalCode: next.replace(/[^\d]/g, '').slice(0, 5) });
                      clearAddressVerification();
                    }}
                    placeholder="94105"
                    maxLength={5}
                    inputMode="numeric"
                    autoComplete="postal-code"
                  />
                </div>
              </div>

              {addressVerificationState !== 'idle' ? (
                <div className={`rounded-[1.25rem] border px-4 py-3 text-[13px] ${addressVerificationState === 'verified' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                  {addressVerificationMessage}
                </div>
              ) : null}

              {addressSuggestedCorrection ? (
                <div className="rounded-[1.5rem] border border-slate-200 bg-white px-4 py-4">
                  <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">Suggested Address</p>
                  <p className="mt-3 text-[14px] leading-6 text-slate-700">
                    {addressSuggestedCorrection.addressLine1}
                    {addressSuggestedCorrection.addressLine2 ? (
                      <>
                        <br />
                        {addressSuggestedCorrection.addressLine2}
                      </>
                    ) : null}
                    <br />
                    {addressSuggestedCorrection.city}, {addressSuggestedCorrection.state} {addressSuggestedCorrection.postalCode}
                  </p>
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => applyVerifiedAddress(addressSuggestedCorrection)}
                      className="h-11 rounded-xl bg-slate-900 px-4 text-sm font-bold text-white transition hover:bg-slate-800"
                    >
                      Use suggested address
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddressSuggestedCorrection(null)}
                      className="h-11 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-700 transition hover:border-slate-300"
                    >
                      Keep editing
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {currentStep === 3 ? (
            <div className="space-y-4">
              <div>
                <p className="mb-2 text-[13px] font-semibold text-slate-700">Birthday</p>
                <div className="grid grid-cols-3 gap-3">
                  <Input
                    value={value.dobMonth}
                    onChange={(next) => {
                      const normalized = normalizeIdentityDobSegment(next, 2);
                      update({ dobMonth: normalized });
                      if (shouldAutoAdvanceIdentityDobSegment(normalized, 2)) dayInputRef.current?.focus();
                    }}
                    placeholder="MM"
                    maxLength={2}
                    inputMode="numeric"
                    className={`text-center ${liveDobError ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                  />
                  <Input
                    inputRef={dayInputRef}
                    value={value.dobDay}
                    onChange={(next) => {
                      const normalized = normalizeIdentityDobSegment(next, 2);
                      update({ dobDay: normalized });
                      if (shouldAutoAdvanceIdentityDobSegment(normalized, 2)) yearInputRef.current?.focus();
                    }}
                    placeholder="DD"
                    maxLength={2}
                    inputMode="numeric"
                    className={`text-center ${liveDobError ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                  />
                  <Input
                    inputRef={yearInputRef}
                    value={value.dobYear}
                    onChange={(next) => {
                      const normalized = normalizeIdentityDobSegment(next, 4);
                      update({ dobYear: normalized });
                      if (shouldAutoAdvanceIdentityDobSegment(normalized, 4)) ssnInputRef.current?.focus();
                    }}
                    placeholder="YYYY"
                    maxLength={4}
                    inputMode="numeric"
                    className={`text-center ${liveDobError ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-100' : ''}`}
                  />
                </div>
                {liveDobError ? (
                  <p role="alert" className="mt-2 text-[13px] font-medium text-rose-700">
                    {liveDobError}
                  </p>
                ) : null}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="text-[13px] font-semibold text-slate-700">Last 4 of SSN</p>
                  <span className="text-[12px] font-medium text-slate-400">Required by Astra</span>
                </div>
                <Input
                  inputRef={ssnInputRef}
                  value={value.ssnLast4}
                  onChange={(next) => update({ ssnLast4: next.replace(/[^\d]/g, '').slice(0, 4) })}
                  placeholder="XXXX"
                  maxLength={4}
                  inputMode="numeric"
                  type="password"
                  autoComplete="off"
                />
              </div>

              <div className="rounded-[1.5rem] border border-[#D7F5F0] bg-[#F2FCFA] px-4 py-4">
                <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-teal-600">Security</p>
                <p className="mt-2 text-[14px] leading-6 text-slate-700">Secured by Astra. Clero never stores your SSN.</p>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-white px-4 py-4">
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-teal-100 text-teal-700">i</div>
                  <p className="text-[15px] font-bold text-slate-900">Consent</p>
                </div>
                <p className="mt-3 text-[14px] leading-6 text-slate-600">
                  I authorize Clero to use Astra to initiate ACH payments from my linked bank account when I approve a payment, and I agree to Astra&apos;s applicable{' '}
                  <a className="font-semibold text-slate-900 underline" href={ASTRA_TERMS_URL} target="_blank" rel="noreferrer">
                    Terms
                  </a>{' '}
                  and{' '}
                  <a className="font-semibold text-slate-900 underline" href={ASTRA_PRIVACY_URL} target="_blank" rel="noreferrer">
                    Privacy Policy
                  </a>.
                </p>
                <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-[1rem] border border-slate-200 px-4 py-3 transition hover:border-slate-300">
                  <input
                    type="checkbox"
                    checked={consentCaptured}
                    onChange={(event) => setConsentCaptured(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-[14px] font-medium text-slate-700">
                    I consent to Astra ACH payment authorization
                  </span>
                </label>
              </div>
            </div>
          ) : null}
        </div>

        <div
          className="shrink-0 border-t border-slate-100 bg-white px-5 pt-4 shadow-[0_-12px_32px_rgba(15,23,42,0.06)] sm:px-6"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => setCurrentStep((step) => Math.max(1, step - 1))}
              disabled={currentStep === 1 || busy}
              className={`h-12 rounded-[1rem] px-5 text-sm font-semibold transition sm:min-w-[120px] ${currentStep === 1 || busy ? 'bg-slate-100 text-slate-400' : 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => void handleContinue()}
              disabled={busy || Boolean(liveDobError)}
              className={`h-12 rounded-[1rem] px-5 text-sm font-bold transition sm:min-w-[180px] ${busy || liveDobError ? 'bg-slate-200 text-slate-400' : 'bg-slate-900 text-white hover:bg-slate-800'}`}
            >
              {busy ? 'Verifying...' : currentStep === TOTAL_STEPS ? 'Finish verification' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayerIdentityVerificationSheet;
